'use client';

import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef } from 'react';
import type { ViduV1Element } from '@/app/types';

type HomeGenerationSubjectUseHandler = (element: ViduV1Element, traceId?: string) => void;

interface HomeGenerationFormActionsContextValue {
  registerSubjectUseHandler: (handler: HomeGenerationSubjectUseHandler) => () => void;
  useSubjectInHomeForm: HomeGenerationSubjectUseHandler;
}

const HomeGenerationFormActionsContext = createContext<HomeGenerationFormActionsContextValue | undefined>(undefined);

export const HomeGenerationFormActionsProvider = ({ children }: { children: ReactNode }) => {
  const subjectUseHandlerRef = useRef<HomeGenerationSubjectUseHandler | null>(null);

  const registerSubjectUseHandler = useCallback((handler: HomeGenerationSubjectUseHandler) => {
    subjectUseHandlerRef.current = handler;

    return () => {
      if (subjectUseHandlerRef.current === handler) {
        subjectUseHandlerRef.current = null;
      }
    };
  }, []);

  const useSubjectInHomeForm = useCallback<HomeGenerationSubjectUseHandler>((element, traceId) => {
    subjectUseHandlerRef.current?.(element, traceId);
  }, []);

  const value = useMemo(
    () => ({
      registerSubjectUseHandler,
      useSubjectInHomeForm,
    }),
    [registerSubjectUseHandler, useSubjectInHomeForm],
  );

  return (
    <HomeGenerationFormActionsContext.Provider value={value}>{children}</HomeGenerationFormActionsContext.Provider>
  );
};

export const useHomeGenerationFormActions = () => useContext(HomeGenerationFormActionsContext);
